<template>
  <v-dialog
    v-model="dialog"
    width="100%"
    max-width="1300px"
    max-height="94%"
    height="100%"
    scrollable
    content-class="mt-16 payment-modal"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn id="par" v-if="type === 'button'" color="secondary" v-bind="attrs" v-on="on">
        <span class="whitespace-normal"><slot></slot></span>
      </v-btn>
      <a v-else href="#" class="" v-bind="attrs" v-on="on">
        <slot></slot>
      </a>
    </template>

    <v-card class="pb-4">
      <v-toolbar flat dense>
        <div class="flex-grow-1"></div>
        <v-btn icon small @click="dialog = false" color="text-gray-400">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-title>
        {{ $t("paymentAssistanceRequest.title") }}
      </v-card-title>
      <v-card-text>
        <p v-if="!success" style="font-size: 16px">
          {{ $t("paymentAssistanceRequest.body") }}
        </p>

        <p v-if="!success">* {{ $t("validate.required") }}</p>
        <p v-if="success">
          <strong>{{ $t("interface.thank_you") }}</strong>
        </p>

        <v-form v-if="!success" v-model="valid">
          <v-text-field
            class="mt-8 field-name"
            v-model.trim="form.name"
            :rules="rules.required"
            :label="'* ' + $t('interface.name')"
            required
            outlined
            @change="clearErrors('field-name')"
          ></v-text-field>

          <div class="flex justify-between -mx-2">
            <v-text-field
              class="px-2"
              :label="'* ' + $t('interface.streetAddress')"
              :value="$store.state.entry.addresses.mailing.street"
              required
              disabled
              outlined
            ></v-text-field>
          </div>
          <div class="flex justify-between -mx-2 mobile-flex-undo">
            <v-text-field
              class="px-2"
              :label="'* ' + $t('interface.city')"
              :value="$store.state.entry.addresses.mailing.city"
              required
              disabled
              outlined
            ></v-text-field>
            <v-text-field
              class="px-2"
              :label="'* ' + $t('interface.state')"
              :value="$store.state.entry.addresses.mailing.state"
              required
              disabled
              outlined
            ></v-text-field>
            <v-text-field
              class="px-2"
              :label="'* ' + $t('interface.postal')"
              :value="$store.state.entry.addresses.mailing.postal"
              required
              disabled
              outlined
            ></v-text-field>
          </div>

          <v-text-field
            class="field-phone"
            v-model="form.phone"
            v-mask="'###-###-####'"
            :rules="rules.phone"
            :label="'* ' + $t('interface.phone')"
            required
            outlined
            @change="clearErrors('field-phone')"
          ></v-text-field>

          <v-text-field
            v-model.trim="form.email"
            :rules="rules.email"
            :label="$t('interface.email')"
            outlined
          ></v-text-field>

          <!--<v-text-field v-model="form.age" :label="$t('interface.age')" :rules="rules.number" outlined></v-text-field>-->
          <v-text-field
            v-model="form.dateOfBirth"
            :rules="[dateOfBirth]"
            :label="'* ' + $t('interface.dateOfBirth')"
            placeholder="mm/dd/yyyy"
            v-mask="'##/##/####'"
            hint="Examples: 01/01/1999, 12/31/1999"
            required
            outlined
            class="field-dateOfBirth"
            @change="clearErrors('field-dateOfBirth')"
          ></v-text-field>
          <!-- Custom HTML message -->
          <div
            v-if="dobMessage"
            class="v-messages theme--light error--text text-sm -mt-6 mb-2"
            v-html="dobMessage"
          ></div>

          <p class="mt-0 mb-4">
            {{ $t("paymentAssistanceRequest.demographic") }}
          </p>

          <v-select
            v-model="form.race"
            :rules="rules.required"
            :items="ethnicityItems"
            item-text="text"
            item-value="value"
            required
            multiple
            :label="'* ' + $t('interface.race')"
            outlined
            persistent-hint
            class="field-race"
            @change="clearErrors('field-race')"
          ></v-select>

          <v-text-field
            v-if="form.race.includes('Other')"
            class="px-2"
            :label="'Other Race'"
            v-model.trim="form.race_other"
            outlined
          ></v-text-field>

          <div class="mobile-field-label">* {{ $t("interface.latin_label") }}</div>
          <v-select
            v-model="form.ethnicity"
            :items="latinItems"
            item-text="text"
            item-value="value"
            required
            :label="'* ' + $t('interface.latin_label')"
            outlined
            persistent-hint
            class="field-ethnicity"
            @change="clearErrors('field-ethnicity')"
          ></v-select>

          <v-select
            v-model="form.gender"
            :items="genderItems"
            :rules="rules.required"
            :label="'* ' + $t('interface.gender')"
            outlined
            class="field-gender"
            @change="clearErrors('field-gender')"
          ></v-select>

          <div class="mobile-field-label">* {{ $t("interface.veteran") }}</div>
          <v-select
            v-model="form.military_veteran"
            :items="yesNoItems"
            :label="'* ' + $t('interface.veteran')"
            :rules="rules.required"
            class="field-military_veteran"
            @change="clearErrors('field-military_veteran')"
            outlined
          ></v-select>

          <div class="mobile-field-label">{{ $t("interface.medical_conditions_label") }}</div>
          <v-select
            v-model="form.medical_conditions"
            :items="medicalConditionsItems"
            multiple
            :label="$t('interface.medical_conditions_label')"
            outlined
          ></v-select>

          <v-text-field
            v-if="form.medical_conditions.includes('Other')"
            class="px-2"
            :label="'Other Medical Conditions'"
            v-model.trim="form.medical_conditions_other"
            outlined
          ></v-text-field>

          <div class="mobile-field-label">* {{ $t("interface.canShop") }}</div>
          <v-select
            v-model="form.can_shop"
            :items="yesNoItems"
            :label="'* ' + $t('interface.canShop')"
            :rules="rules.required"
            class="field-can_shop"
            @change="clearErrors('field-can_shop')"
            outlined
          ></v-select>

          <div class="mobile-field-label">* {{ $t("interface.canPrepareMeals") }}</div>
          <v-select
            v-model="form.prepare_meals"
            :items="yesNoItems"
            :label="'* ' + $t('interface.canPrepareMeals')"
            :rules="rules.required"
            outlined
            class="field-prepare_meals"
            @change="clearErrors('field-prepare_meals')"
          ></v-select>

          <div class="mobile-field-label">* {{ $t("interface.canHandleFinances") }}</div>
          <v-select
            v-model="form.handle_finances"
            :items="yesNoItems"
            :label="'* ' + $t('interface.canHandleFinances')"
            :rules="rules.required"
            outlined
            class="field-handle_finances"
            @change="clearErrors('field-handle_finances')"
          ></v-select>

          <div class="mobile-field-label">* {{ $t("interface.canPublicTransportation") }}</div>
          <v-select
            v-model="form.public_transportation"
            :items="yesNoItems"
            :label="'* ' + $t('interface.canPublicTransportation')"
            :rules="rules.required"
            class="field-public_transportation"
            @change="clearErrors('field-public_transportation')"
            outlined
          ></v-select>

          <v-radio-group v-model="form.householdStatus" :mandatory="true">
            <v-radio :label="$t(`household.forSelf.livesAloneOption`)" value="single"></v-radio>
            <v-radio :label="$t(`household.forSelf.livesWithOthersOption`)" value="multiple"></v-radio>
          </v-radio-group>
          <div class="mobile-field-label">{{ $t("household.form.householdMonthlyIncome") }}</div>
          <v-select
            v-if="form.householdStatus === 'single'"
            v-model="form.incomeRange"
            :items="householdIncome"
            :rules="rules.required"
            :label="'* ' + $t('household.form.householdMonthlyIncome')"
            outlined
            class="field-incomeRange"
            @change="clearErrors('field-incomeRange')"
          ></v-select>
          <v-select
            v-if="form.householdStatus === 'multiple'"
            v-model="form.incomeRange"
            :items="householdIncome"
            :rules="rules.required"
            :label="'* ' + $t('household.form.householdMonthlyIncome')"
            outlined
            class="field-incomeRange"
            @change="clearErrors('field-incomeRange')"
          ></v-select>

          <div class="mobile-field-label">* {{ $t("interface.canContribute") }}</div>
          <v-select
            v-model="form.cost_toward_meal"
            :items="canContribute"
            :label="'* ' + $t('interface.canContribute')"
            :rules="rules.required"
            outlined
            class="field-cost_toward_meal"
            @change="clearErrors('field-cost_toward_meal')"
          ></v-select>

          <!-- <v-checkbox
            class="-mt-1 mb-2"
            v-model="form.affectedByCovid"
            :label="$t('paymentAssistanceRequest.affectedByCovid')"
          ></v-checkbox> -->

          <v-select
            v-model="form.primaryLanguage"
            :items="languageItems"
            :rules="rules.required"
            :label="'* ' + $t('interface.primaryLanguageSpoken')"
            outlined
            class="field-primaryLanguage"
            @change="clearErrors('field-primaryLanguage')"
          ></v-select>

          <v-text-field
            v-if="form.primaryLanguage === 'Other'"
            v-model.trim="form.otherLanguage"
            :label="$t('interface.otherLanguage')"
            outlined
          ></v-text-field>

          <div class="flex justify-between -mx-2 mobile-flex-undo">
            <v-text-field
              class="px-2"
              :label="$t('interface.emergencyName')"
              v-model.trim="form.emergency_contact_name"
              outlined
            ></v-text-field>
            <v-text-field
              class="px-2"
              :label="$t('interface.emergencyPhone')"
              v-model="form.emergency_phone"
              v-mask="'###-###-####'"
              outlined
            ></v-text-field>
            <div class="mobile-field-label" style="padding-left: 10px">{{ $t("interface.emergencyRelationship") }}</div>
            <v-text-field
              class="px-2"
              :label="$t('interface.emergencyRelationship')"
              v-model.trim="form.emergency_contact_relationship"
              outlined
            ></v-text-field>
          </div>

          <v-textarea
            v-model.trim="form.commentsAndQuestions"
            :label="$t('paymentAssistanceRequest.commentsAndQuestions')"
            outlined
          ></v-textarea>

          <h4 class="mt-6 mb-4">
            {{ $t("interface.otherFormsOfAssistance") }}
          </h4>

          <div class="mobile-field-label" style="padding-left: 10px">
            {{ $t("interface.assistanceWIC") }}
          </div>
          <div class="flex justify-between -mx-2">
            <v-select
              v-model="form.other_assistance_wic"
              :items="assistanceOptions"
              :label="$t('interface.assistanceWIC')"
              class="px-2"
              clearable
              outlined
            ></v-select>
          </div>

          <div class="flex justify-between -mx-2 mobile-flex-undo">
            <v-select
              v-model="form.other_assistance_snap"
              :items="assistanceOptions"
              :label="$t('interface.assistanceSNAP')"
              class="px-2"
              clearable
              outlined
            ></v-select>
            <v-select
              v-model="form.other_assistance_medicaid"
              :items="assistanceOptions"
              :label="$t('interface.assistanceMedicaid')"
              class="px-2"
              clearable
              outlined
            ></v-select>
          </div>

          <div class="flex justify-between -mx-2 mobile-flex-undo">
            <v-select
              v-model="form.other_assistance_elderly"
              :items="assistanceOptions"
              :label="$t('interface.assistanceElderly')"
              class="px-2"
              clearable
              outlined
            ></v-select>
            <v-select
              v-model="form.other_assistance_disability"
              :items="assistanceOptions"
              :label="$t('interface.assistanceDisability')"
              class="px-2"
              clearable
              outlined
            ></v-select>
            <v-select
              v-model="form.other_assistance_foodshelf"
              :items="assistanceOptions"
              :label="$t('interface.assistanceFoodShelf')"
              class="px-2"
              clearable
              outlined
            ></v-select>
          </div>

          <div class="flex justify-between -mx-2 mobile-flex-undo">
            <v-select
              v-model="form.other_assistance_freemeal"
              :items="assistanceOptions"
              :label="$t('interface.assistanceFreeMeal')"
              class="px-2"
              clearable
              outlined
            ></v-select>
            <v-select
              v-model="form.other_assistance_veterans"
              :items="assistanceOptions"
              :label="$t('interface.assistanceVeterans')"
              class="px-2"
              clearable
              outlined
            ></v-select>
            <v-select
              v-model="form.other_assistance_rent"
              :items="assistanceOptions"
              :label="$t('interface.assistanceRent')"
              class="px-2"
              clearable
              outlined
            ></v-select>
            <v-select
              v-model="form.other_assistance_heating"
              :items="assistanceOptions"
              :label="$t('interface.assistanceHeating')"
              class="px-2"
              clearable
              outlined
            ></v-select>
          </div>

          <v-checkbox
            class="-mt-1 mb-2 field-use_of_information"
            @change="clearErrors('field-use_of_information')"
            v-model="form.use_of_information"
            :rules="rules.required"
            :label="$t('interface.useOfInformation')"
          ></v-checkbox>
          <v-btn v-on:click="submit" color="primary" :loading="isSubmitting" :disabled="isSubmitting">
            {{ $t("interface.submit") }}
          </v-btn>
        </v-form>
        <div v-else>
          <p class="custom-messages-font-size" v-html="successHtml"></p>
          <button
            type="button"
            class="mt-4 v-btn v-btn--contained theme--light v-size--default primary"
            @click="goHome()"
          >
            <span class="v-btn__content">{{ $t('paymentAssistanceRequest.done') }}</span>
          </button>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import DOMPurify from "dompurify";
export default {
  name: "PaymentAssistanceRequest",
  components: {},
  props: {
    type: {
      type: String,
      default: "button",
    },
  },
  data() {
    return {
      dialog: false,
      valid: false,
      success: false,
      isSubmitting: false,
      vueAppSiteUrl:
        process.env.NODE_ENV === "production"
          ? "https://meals-on-wheels.com"
          : "https://staging.mammoth-yard.flywheelsites.com",
      form: {
        name: "",
        phone: "",
        email: "",
        dateOfBirth: "",
        ethnicity: "",
        race: "",
        race_other: "",
        householdStatus: "",
        incomeRange: "",
        affectedByCovid: "",
        primaryLanguage: "",
        otherLanguage: "",
        commentsAndQuestions: "",
        gender: "",
        military_veteran: "",
        medical_conditions: "",
        medical_conditions_other: "",
        can_shop: "",
        prepare_meals: "",
        handle_finances: "",
        public_transportation: "",
        cost_toward_meal: "",
        emergency_phone: "",
        emergency_contact_name: "",
        emergency_contact_relationship: "",
        use_of_information: "",
        other_assistance_wic: null,
        other_assistance_snap: null,
        other_assistance_medicaid: null,
        other_assistance_elderly: null,
        other_assistance_disability: null,
        other_assistance_foodshelf: null,
        other_assistance_freemeal: null,
        other_assistance_veterans: null,
        other_assistance_rent: null,
        other_assistance_heating: null,
      },
      dobMessage: "",
      rules: {
        required: [(v) => !!v || this.$t("validate.fieldRequired")],
        number: [(v) => v.search(/^\d+$/) > -1 || this.$t("validate.numberRequired")],
        email: [
          (v) => {
            return (
              !v ||
              v.search(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              ) > -1 ||
              this.$t("validate.validEmail")
            );
          },
        ],
        ethnicity: [(v) => v.length > 0 || this.$t("validate.ethnicityRequired")],
        phone: [
          (v) => {
            return (
              v.search(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/) > -1 || this.$t("validate.validPhone")
            );
          },
        ],
      },
    };
  },
  computed: {
    genderItems() {
      const genders = this.$i18n.messages?.[this.$i18n.locale]?.interface?.genders;

      return Array.isArray(genders) ? genders : [];
    },
    latinItems() {
      const latins = this.$i18n.messages?.[this.$i18n.locale]?.interface?.latins;
      return Array.isArray(latins) ? latins : [];
    },
    ethnicityItems() {
      const ethnicities = this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest?.ethnicity;
      return Array.isArray(ethnicities) ? ethnicities : [];
    },
    yesNoItems() {
      const options = this.$i18n.messages?.[this.$i18n.locale]?.interface.option;
      return Array.isArray(options) ? options : [];
    },
    assistanceOptions() {
      const options = this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest?.assistanceOptions;
      return Array.isArray(options) ? options : [];
    },
    medicalConditionsItems() {
      const medicalConditions = this.$i18n.messages?.[this.$i18n.locale]?.interface.medical_conditions;
      return Array.isArray(medicalConditions) ? medicalConditions : [];
    },
    languageItems() {
      const languages = this.$i18n.messages?.[this.$i18n.locale]?.interface.languages;
      return Array.isArray(languages) ? languages : [];
    },
    householdIncome() {
      let householdIncomes = this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest?.houseHoldIncomeSingle;
      if (this.form.householdStatus !== "single") {
        householdIncomes = this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest?.houseHoldIncomeMultiple;
      }
      return Array.isArray(householdIncomes) ? householdIncomes : [];
    },
    canContribute() {
      const canContribute = this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest?.canContribute;
      return Array.isArray(canContribute) ? canContribute : [];
    },
    successHtml() {
      const raw = this.$t("paymentAssistanceRequest.successHtml", {
        mowPhoneHref: "tel:+16126233363",
        mowPhoneDisplay: "612-623-3363",
        mowEmailHref: "mailto:info@meals-on-wheels.com",
        mowEmailDisplay: "info@meals-on-wheels.com",
        foodHelpline: this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest.foodHelpline,
        helplineUrl: "https://www.hungersolutions.org/programs/mn-food-helpline/",
        helplinePhoneHref: "tel:+18887111151",
        helplinePhoneDisplay: "1-888-711-1151",
        phone: this.$i18n.messages?.[this.$i18n.locale]?.interface.phone,
      });

      return DOMPurify.sanitize(raw, {
        USE_PROFILES: { html: true },
        ALLOWED_TAGS: ["a", "br", "strong", "em"],
        ALLOWED_ATTR: ["href", "title", "target", "rel"],
      });
    },
    dobErrorMessage() {
      const raw = this.$t("paymentAssistanceRequest.dobError", {
        mowPhoneHref: "tel:+16126233363",
        mowPhoneDisplay: "612-623-3363",
        mowEmailHref: "mailto:info@meals-on-wheels.com",
        mowEmailDisplay: "info@meals-on-wheels.com",
        foodHelpline: this.$i18n.messages?.[this.$i18n.locale]?.paymentAssistanceRequest.foodHelpline,
        helplineUrl: "https://www.hungersolutions.org/programs/mn-food-helpline/",
        helplinePhoneHref: "tel:+18887111151",
        helplinePhoneDisplay: "1-888-711-1151",
        phone: this.$i18n.messages?.[this.$i18n.locale]?.interface.phone,
      });

      return DOMPurify.sanitize(raw, {
        USE_PROFILES: { html: true },
        ALLOWED_TAGS: ["a"],
        ALLOWED_ATTR: ["href", "title", "target", "rel"],
      });
    },
  },
  methods: {
    submit() {
      if (this.formValid()) {
        // Set isSubmitting to true when starting submission
        this.isSubmitting = true;

        fetch(`${process.env.VUE_APP_API_URL}/payment-assistance-requests`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: this.$store.state.id,
            entry: {
              ...{
                address: this.$store.state.entry.addresses.mailing,
                district: this.$store.state.entry.district,
                name:
                  this.$store.state.entry.personalInformation.firstName +
                  " " +
                  this.$store.state.entry.personalInformation.lastName,
              },
              ...this.form,
            },
          }),
        })
          .then(() => {
            this.success = true;
            // Go back to the step 2?
            this.$store.commit("setStep", 2);
            window.scrollTo(0, 0);
            // Reset isSubmitting
            this.isSubmitting = false;
          })
          .catch((error) => {
            console.error(`Error: `, error);
            // Reset isSubmitting even on error
            this.isSubmitting = false;
          });
      }
    },
    goHome() {
      window.top.location.href = this.vueAppSiteUrl;
    },
    clearErrors: (classString) => {
      const fieldElements = document.getElementsByClassName(classString);
      for (const fieldElement of Object.entries(fieldElements)) {
        const inputSlots = fieldElement[1].getElementsByClassName("v-input__slot");
        for (const inputSlot of Object.entries(inputSlots)) {
          inputSlot[1].classList.remove("field-missing");
        }
      }
    },
    formValid() {
      let requiredFields = [
        { field: this.form.name, message: "Name", class: "field-name" },
        { field: this.form.phone, message: "Phone", class: "field-phone" },
        { field: this.form.dateOfBirth, message: "Date of birth", class: "field-dateOfBirth" },
        { field: this.form.race, message: "Race", class: "field-race" },
        { field: this.form.ethnicity, message: "Ethnicity", class: "field-ethnicity" },
        {
          field: this.form.can_shop,
          message: "Can you shop for food and other things you need without help?",
          class: "field-can_shop",
        },
        {
          field: this.form.prepare_meals,
          message: "Can you prepare meals for yourself without help?",
          class: "field-prepare_meals",
        },
        {
          field: this.form.handle_finances,
          message: "Can you handle your own finances, like keeping track of bills without help?",
          class: "field-handle_finances",
        },
        {
          field: this.form.public_transportation,
          message: "Can you use public transportation or drive beyond walking distances without help?",
          class: "field-public_transportation",
        },
        { field: this.form.incomeRange, message: "Monthly income range", class: "field-incomeRange" },
        { field: this.form.cost_toward_meal, message: "Meal contribution", class: "field-cost_toward_meal" },
        { field: this.form.primaryLanguage, message: "Primary language", class: "field-primaryLanguage" },
        { field: this.form.use_of_information, message: "Use of information", class: "field-use_of_information" },
      ];

      let errorMessageString = "";
      let valid = true;

      let classRemovals = document.getElementsByClassName("v-input__slot");
      for (const classRemoval of Object.entries(classRemovals)) {
        classRemoval[1].classList.remove("field-missing");
      }

      for (const requiredField of Object.entries(requiredFields)) {
        if (typeof requiredField[1].field === "undefined" || !requiredField[1].field) {
          errorMessageString = errorMessageString + requiredField[1].message + "\r\n";
          let fieldElements = document.getElementsByClassName(requiredField[1].class);
          for (const fieldElement of Object.entries(fieldElements)) {
            let inputSlots = fieldElement[1].getElementsByClassName("v-input__slot");
            for (const inputSlot of Object.entries(inputSlots)) {
              inputSlot[1].classList.add("field-missing");
            }
          }
        }
      }

      if (errorMessageString) {
        valid = false;
        alert(this.$t("validate.fieldErrorMessage") + " \r\n\r\n" + errorMessageString);
      }
      return valid;
    },
    dateOfBirth(value) {
      const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[1-2][0-9]|3[01])\/\d{4}$/;
      if (value.search(regex) < 0) {
        this.dobMessage = "";
        return this.$t("validate.dob_required");
      } else {
        const dateArr = value.split("/");
        const dob = new Date(dateArr[2], dateArr[0] - 1, dateArr[1]);
        const dobTimestamp = Math.floor(dob.getTime() / 1000);

        const minDate = Math.floor(new Date(1900, 0, 1, 0, 0, 0, 0).getTime() / 1000);

        const sixtyYearsAgo = new Date();
        sixtyYearsAgo.setFullYear(sixtyYearsAgo.getFullYear() - 60);
        sixtyYearsAgo.setDate(sixtyYearsAgo.getDate() - 1);
        sixtyYearsAgo.setHours(0, 0, 0, 0);
        const maxDate = Math.floor(sixtyYearsAgo.getTime() / 1000);

        const maxDateObj = new Date(maxDate * 1000);
        const formattedMaxDate = `${String(maxDateObj.getMonth() + 1).padStart(2, "0")}/${String(
          maxDateObj.getDate()
        ).padStart(2, "0")}/${maxDateObj.getFullYear()}`;

        if (dobTimestamp < minDate) {
          this.dobMessage = "";
          return this.$t("validate.dob_range") + formattedMaxDate;
        }

        if (dobTimestamp > maxDate) {
          this.dobMessage = this.dobErrorMessage;
          return false; // Keeps text field in error state
        }

        this.dobMessage = "";
        return true;
      }
    },
  },
};
</script>

<style scoped>
/* Add styles for loading state */
.v-btn.v-btn--loading {
  pointer-events: none;
}

/* Add custom loading spinner color if needed */
.v-btn.v-btn--loading .v-progress-circular {
  color: white;
}
</style>
